import React, { useCallback } from 'react';
import { toast } from "@/components/ui/sonner";
import { useOptionalFieldValidation } from '@/components/validation';
import { useDefaultExtendedTiers } from '@/hooks/useDefaultExtendedTiers';
import { useUserConfigState, useUserConfigDispatch } from '@/context/UserConfigContext';
import { HolidayConfigForm } from '@/components/config_forms/holiday/HolidayConfigForm';
import type { UserConfigurationState } from '@/types/userConfiguration';
import type { ExtendedTier } from '@/types/ExtendedTier';
import { cn } from '@/lib/utils';

interface UserHolidayConfigProps {
  noCard?: boolean;
  roles?: UserConfigurationState['roles'];
  rateMultipliers?: UserConfigurationState['rateMultipliers'];
}

export const HolidayConfig: React.FC<UserHolidayConfigProps> = ({
  noCard,
  roles: propRoles,
  rateMultipliers: propRateMultipliers,
}) => {
  const state = useUserConfigState();
  const defaultExtendedTiers = useDefaultExtendedTiers('holiday');
  const { updateBillingConfig } = useUserConfigDispatch();
  const onSelectedCatalogIdsChange = useCallback((ids: string[]) => {
    updateBillingConfig({ holidayCatalogIds: ids });
    toast.success(`${ids.length} holidays selected.`);
  }, [updateBillingConfig]);

  const onSelectedCustomIdsChange = useCallback((ids: string[]) => {
    updateBillingConfig({ holidayCustomIds: ids });
  }, [updateBillingConfig]);

  const handleExtendedOvertimeEnabledChange = useCallback((v: boolean) => {
    updateBillingConfig({ holidayExtendedOvertimeEnabled: v });
  }, [updateBillingConfig]);

  const handleExtendedOvertimeTiersChange = useCallback((tiers: ExtendedTier[]) => {
    updateBillingConfig({ holidayExtendedOvertimeTiers: tiers });
  }, [updateBillingConfig]);

  const onApplyHolidayPremiumChange = useCallback((checked: boolean) => {
    updateBillingConfig({ applyHolidayPremium: checked });
    toast.success(checked ? "Holiday premium enabled" : "Holiday premium disabled");
  }, [updateBillingConfig]);

  const onHolidayPremiumTypeChange = useCallback((value: UserConfigurationState['holidayPremiumType']) => {
    const updates: Partial<UserConfigurationState> = { holidayPremiumType: value };
    if (value === 'hourly_premium' && !state.holidayHourlyPremiumType) {
      updates.holidayHourlyPremiumType = 'fixed';
    }
    updateBillingConfig(updates);
    toast.success(`Holiday premium type set to ${value.replace(/_/g, ' ')}`);
  }, [updateBillingConfig, state.holidayHourlyPremiumType]);

  const onHolidayOffsetAmountChange = useCallback((amount: number) => {
    updateBillingConfig({ holidayOvertimeOffset: amount });
  }, [updateBillingConfig]);

  const onHolidayMultiplierFloorCodeChange = useCallback((code: string | null) => {
    updateBillingConfig({ holidayFloorTierCode: code });
    const multiplierName = state.rateMultipliers.find(m => m.code === code)?.name;
    if(multiplierName) toast.success(`Holiday floor multiplier set to "${multiplierName}"`);
  }, [updateBillingConfig, state.rateMultipliers]);

  const onHolidayMultiplierFloorCustomValueChange = useCallback((value: number | null) => {
    updateBillingConfig({ holidayFloorTierCustomValue: value });
  }, [updateBillingConfig]);

  // Field validations via ValidationProvider schemas
  const fixedAmountVal = useOptionalFieldValidation('holidayFixedPremiumAmount');
  const premiumVal = useOptionalFieldValidation('holidayHourlyPremiumValue');

  const buildInputProps = (
    fieldKey: string,
    value: number | null,
    onChange: (value: number | null) => void,
    validation: ReturnType<typeof useOptionalFieldValidation>,
  ): Record<string, unknown> => ({
    'data-validation-key': fieldKey,
    value: value ?? '',
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value ? parseFloat(e.target.value) : null;
      onChange(v);
    },
    onBlur: validation.onBlur,
    className: cn(validation.hasError && "border-destructive focus-visible:ring-destructive focus-visible:border-destructive"),
  });

  const fixedSurchargeAmountInputProps = buildInputProps(
    'holidayFixedPremiumAmount',
    state.holidayFixedPremiumAmount,
    useCallback((value: number | null) => updateBillingConfig({ holidayFixedPremiumAmount: value }), [updateBillingConfig]),
    fixedAmountVal,
  );

  const premiumValueInputProps = buildInputProps(
    'holidayHourlyPremiumValue',
    state.holidayHourlyPremiumValue ?? null,
    useCallback((value: number | null) => updateBillingConfig({ holidayHourlyPremiumValue: value }), [updateBillingConfig]),
    premiumVal,
  );

  const onHolidayHourlyPremiumTypeChange = useCallback((type: 'percentage' | 'fixed') => {
    updateBillingConfig({ holidayHourlyPremiumType: type });
  }, [updateBillingConfig]);

  const onHolidayPremiumBasisChange = useCallback((basis: 'base_rate' | 'ot_inclusive') => {
    updateBillingConfig({ holidayPremiumBasis: basis });
    toast.success(basis === 'ot_inclusive' ? "Holiday premium will now compound" : "Holiday premium will be additive");
  }, [updateBillingConfig]);

  const onHolidayApplyOnHalfDaysChange = useCallback((checked: boolean) => {
    updateBillingConfig({ holidayApplyOnHalfDays: checked });
  }, [updateBillingConfig]);

  const onHolidayApplicableRoleIdsChange = useCallback((roleIds: string[] | null) => {
    updateBillingConfig({ holidayApplicableRoleIds: roleIds });
  }, [updateBillingConfig]);

  return (
    <HolidayConfigForm
      title="Holiday Premium"
      description="Define how you're compensated for working on holidays."
      isCustomized={false}
      applyHolidayPremium={state.applyHolidayPremium}
      onApplyHolidayPremiumChange={onApplyHolidayPremiumChange}
      holidayPremiumType={state.holidayPremiumType}
      onHolidayPremiumTypeChange={onHolidayPremiumTypeChange}
      holidayOffsetAmount={state.holidayOvertimeOffset ?? 1}
      onHolidayOffsetAmountChange={onHolidayOffsetAmountChange}
      holidayMultiplierFloorCode={state.holidayFloorTierCode}
      holidayMultiplierFloorCustomValue={state.holidayFloorTierCustomValue}
      onHolidayMultiplierFloorCodeChange={onHolidayMultiplierFloorCodeChange}
      onHolidayMultiplierFloorCustomValueChange={onHolidayMultiplierFloorCustomValueChange}
      holidayFixedPremiumAmount={state.holidayFixedPremiumAmount}
      rateMultipliers={propRateMultipliers ?? state.rateMultipliers}
      roles={propRoles ?? state.roles}
      holidaySelection={{
        selectedCatalogIds: state.holidayCatalogIds ?? [],
        selectedCustomIds: state.holidayCustomIds ?? [],
        onSelectedCatalogIdsChange,
        onSelectedCustomIdsChange,
      }}
      fixedSurchargeAmountProps={fixedSurchargeAmountInputProps}
      fixedSurchargeAmountError={fixedAmountVal.errorElement}
      holidayHourlyPremiumType={state.holidayHourlyPremiumType}
      onHolidayHourlyPremiumTypeChange={onHolidayHourlyPremiumTypeChange}
      holidayUpliftPercentage={state.holidayHourlyPremiumType === 'percentage' ? (state.holidayHourlyPremiumValue ?? null) : null}
      holidayUpliftAmount={state.holidayHourlyPremiumType === 'fixed' ? (state.holidayHourlyPremiumValue ?? null) : null}
      holidayUpliftPercentageProps={premiumValueInputProps}
      holidayUpliftPercentageError={premiumVal.errorElement}
      holidayApplyPremiumAfterOvertime={state.holidayPremiumBasis === 'ot_inclusive'}
      onHolidayApplyPremiumAfterOvertimeChange={(checked) => onHolidayPremiumBasisChange(checked ? 'ot_inclusive' : 'base_rate')}
      holidayApplyOnHalfDays={state.holidayApplyOnHalfDays}
      onHolidayApplyOnHalfDaysChange={onHolidayApplyOnHalfDaysChange}
      holidayApplicableRoleIds={state.holidayApplicableRoleIds}
      onHolidayApplicableRoleIdsChange={onHolidayApplicableRoleIdsChange}
      holidayUpliftAmountProps={premiumValueInputProps}
      holidayUpliftAmountError={premiumVal.errorElement}
      noCard={noCard}
      context="user"
      hideToggle={true}
      extendedOvertimeEnabled={state.holidayExtendedOvertimeEnabled ?? false}
      onExtendedOvertimeEnabledChange={handleExtendedOvertimeEnabledChange}
      extendedOvertimeTiers={state.holidayExtendedOvertimeTiers ?? []}
      onExtendedOvertimeTiersChange={handleExtendedOvertimeTiersChange}
      defaultExtendedTiers={defaultExtendedTiers}
    />
  );
};
